import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Eye, EyeOff, Mail, Lock, AlertCircle, Loader, Shield } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState({})
  const { signIn, signOut, loading, error } = useAuth()
  const navigate = useNavigate()

  const validateForm = () => {
    const newErrors = {}
    
    if (!formData.email) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid'
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) return
    
    const { data, error } = await signIn(formData.email, formData.password)
    
    if (!error && data.user) {
      // Check if user profile exists and get approval status
      const { data: profile } = await supabase
        .from('users')
        .select('approved, user_type')
        .eq('id', data.user.id)
        .single()
      
      if (profile) {
        // Check approval status for government employees
        if (profile.user_type === 'government_employee') {
          try {
            if (profile.approved === false) {
              setErrors({ general: 'Your registration is pending admin approval. Please wait for approval before logging in.' })
              await signOut() // Sign out the user since they're not approved yet
              return
            }
          } catch (approvalCheckError) {
            console.error('Approval status check error (allowing login for compatibility):', approvalCheckError)
            // Continue with login if approval check fails (backward compatibility)
          }
        }
      }
      
      navigate('/dashboard')
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10"></div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 p-8 space-y-8"
        >
          {/* Header */}
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="mx-auto h-16 w-16 bg-gradient-to-br from-cyber-600 to-cyber-700 rounded-2xl flex items-center justify-center shadow-lg mb-6"
            >
              <Shield className="h-8 w-8 text-white" />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h1 className="text-3xl font-bold bg-gradient-to-r from-gov-800 to-cyber-700 bg-clip-text text-transparent mb-2">
                Welcome Back
              </h1>
              <p className="text-gov-600 font-medium">
                Sign in to your CyberGov account
              </p>
            </motion.div>
          </div>
        
          {/* Form */}
          <motion.form
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="space-y-6"
            onSubmit={handleSubmit}
          >
            {error && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-red-50/80 backdrop-blur-sm border border-red-200 rounded-xl p-4 flex items-center space-x-3"
              >
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700 font-medium">{error}</p>
              </motion.div>
            )}
            
            {errors.general && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-yellow-50/80 backdrop-blur-sm border border-yellow-200 rounded-xl p-4 flex items-center space-x-3"
              >
                <AlertCircle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                <p className="text-sm text-yellow-700 font-medium">{errors.general}</p>
              </motion.div>
            )}
          
            <div className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gov-700 mb-2">
                  Email Address
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className={`h-5 w-5 transition-colors ${
                      errors.email ? 'text-red-400' : 'text-gov-400 group-focus-within:text-cyber-500'
                    }`} />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`block w-full pl-12 pr-4 py-3.5 border-2 rounded-xl focus:outline-none focus:ring-4 focus:ring-cyber-500/20 focus:border-cyber-500 transition-all duration-200 font-medium placeholder:text-gov-400 ${
                      errors.email 
                        ? 'border-red-300 bg-red-50/50 focus:border-red-500 focus:ring-red-500/20' 
                        : 'border-gov-200 bg-white/50 hover:border-gov-300'
                    }`}
                    placeholder="your.email@agency.gov"
                  />
                </div>
                {errors.email && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-2 text-sm text-red-600 font-medium flex items-center space-x-1"
                  >
                    <AlertCircle className="h-4 w-4" />
                    <span>{errors.email}</span>
                  </motion.p>
                )}
              </div>
            
              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gov-700 mb-2">
                  Password
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className={`h-5 w-5 transition-colors ${
                      errors.password ? 'text-red-400' : 'text-gov-400 group-focus-within:text-cyber-500'
                    }`} />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`block w-full pl-12 pr-12 py-3.5 border-2 rounded-xl focus:outline-none focus:ring-4 focus:ring-cyber-500/20 focus:border-cyber-500 transition-all duration-200 font-medium placeholder:text-gov-400 ${
                      errors.password 
                        ? 'border-red-300 bg-red-50/50 focus:border-red-500 focus:ring-red-500/20' 
                        : 'border-gov-200 bg-white/50 hover:border-gov-300'
                    }`}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-4 flex items-center hover:bg-gov-50 rounded-r-xl transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gov-400 hover:text-gov-600 transition-colors" />
                    ) : (
                      <Eye className="h-5 w-5 text-gov-400 hover:text-gov-600 transition-colors" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-2 text-sm text-red-600 font-medium flex items-center space-x-1"
                  >
                    <AlertCircle className="h-4 w-4" />
                    <span>{errors.password}</span>
                  </motion.p>
                )}
              </div>
            </div>
          
             <div className="flex items-center justify-center">
               <Link
                 to="/forgot-password"
                 className="text-sm font-medium text-cyber-600 hover:text-cyber-700 transition-colors hover:underline"
               >
                 Forgot your password?
               </Link>
             </div>
             
             <motion.button
               type="submit"
               disabled={loading}
               whileHover={{ scale: loading ? 1 : 1.02 }}
               whileTap={{ scale: loading ? 1 : 0.98 }}
               className="group relative w-full flex justify-center py-4 px-6 border border-transparent text-base font-semibold rounded-xl text-white bg-gradient-to-r from-cyber-600 to-cyber-700 hover:from-cyber-700 hover:to-cyber-800 focus:outline-none focus:ring-4 focus:ring-cyber-500/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-200 shadow-lg hover:shadow-xl"
             >
               {loading ? (
                 <Loader className="h-5 w-5 animate-spin" />
               ) : (
                 <span className="flex items-center space-x-2">
                   <span>Sign In</span>
                   <Lock className="h-4 w-4" />
                 </span>
               )}
             </motion.button>
           </motion.form>
           
           {/* Footer */}
           <motion.div
             initial={{ opacity: 0 }}
             animate={{ opacity: 1 }}
             transition={{ delay: 0.5 }}
             className="text-center pt-6 border-t border-gov-200"
           >
             <p className="text-sm text-gov-600">
               Don't have an account?{' '}
               <Link
                 to="/register"
                 className="font-semibold text-cyber-600 hover:text-cyber-700 transition-colors hover:underline"
               >
                 Create one here
               </Link>
             </p>
           </motion.div>
         </motion.div>
       </motion.div>
     </div>
  )
}

export default Login